/**
 * BoardCanvas - Production-ready canvas using direct Konva.js for performance
 * 
 * This is a thin React wrapper that:
 * - Mounts a KonvaCanvas instance on a container ref
 * - Exposes callbacks for UI components
 * - Manages React state for toolbars and overlays
 * 
 * All canvas rendering and interactions are handled directly by Konva.js,
 * eliminating React reconciliation overhead for canvas operations.
 */

import React from 'react';
import { SelectionProvider } from './editing/SelectionContext';
import { ModeToggle, ModernToolbar, CanvasContextMenu, ZoomControls } from './components';
import { CanvasOverlays } from './components/CanvasOverlays';
import { CanvasToolbars } from './components/CanvasToolbars';
import { useCanvasState } from './hooks/canvas/useCanvasState';
import { useCanvasInitialization } from './hooks/canvas/useCanvasInitialization';
import { useElementRendering } from './hooks/canvas/useElementRendering';
import { useKeyboardShortcuts } from './hooks/canvas/useKeyboardShortcuts';
import { useContextMenu } from './hooks/canvas/useContextMenu';
import { useCanvasHelpers } from './hooks/canvas/useCanvasHelpers';
import { BoardCanvasInnerProps, CanvasApi, CanvasMode } from './BoardCanvas.types';
import { EditableElement, TextStyle } from './editing/types';

const BoardCanvasInner: React.FC<BoardCanvasInnerProps> = (props) => {
  const {
    mode: initialMode = 'view',
    initialElements = [],
    enableRotation = true,
    enableResize = true,
    keepAspectRatio = false,
    showFormatToolbar = true,
    enableAlignment = true,
    width = 1200,
    height = 800,
    backgroundColor = '#F5F5F5',
    onModeChange,
    onElementsChange,
    onCanvasReady,
  } = props;

  // Initialize state
  const state = useCanvasState(initialMode, initialElements);

  // Initialize canvas and managers
  useCanvasInitialization(props, state);

  // Render elements
  useElementRendering(state, width, height);

  // Get helpers
  const helpers = useCanvasHelpers(state, onModeChange);

  // Setup keyboard shortcuts
  useKeyboardShortcuts(state, helpers.handleDelete);

  // Setup context menu
  const contextMenu = useContextMenu(state, helpers.handleDelete);

  return (
    <div 
      style={{ 
        position: 'relative', 
        width: '100%', 
        height: `${height}px`,
        cursor: helpers.getCursor(),
      }}
      onContextMenu={contextMenu.handleContextMenu}
    >
      {/* Mode Toggle */}
      <ModeToggle
        mode={state.mode}
        onModeChange={helpers.handleModeChange}
        leftOffset="20px"
      />

      {/* Modern Toolbar - Edit Mode Only */}
      {state.isEditMode && (
        <ModernToolbar
          currentMode={state.createMode}
          onModeChange={(mode: any) => {
            state.setConnectorMode(null);
            state.setCreateMode(mode);
            state.creationManagerRef.current?.setMode(mode);
            
            // Show cursor preview if mouse is over canvas and in create mode
            if (state.canvasRef.current && mode.type !== 'select' && mode.type !== 'pan') {
              const stage = state.canvasRef.current.getStage();
              if (stage) {
                const pointerPos = stage.getPointerPosition();
                if (pointerPos) {
                  const pos = state.canvasRef.current.screenToLayer(pointerPos);
                  state.creationManagerRef.current?.showCursorPreview(pos.x, pos.y);
                }
              }
            }
          }}
          connectorMode={state.connectorMode}
          onConnectorModeChange={(mode: any) => {
            state.setConnectorMode(mode);
            state.connectorManagerRef.current?.setConnectorMode(mode);
            if (mode) {
              state.setCreateMode({ type: 'select' });
            }
          }}
        />
      )}

      {/* Zoom Controls */}
      {state.zoomControls && (
        <ZoomControls
          scale={state.zoomControls.scale}
          onZoomIn={state.zoomControls.zoomIn}
          onZoomOut={state.zoomControls.zoomOut}
          onFullscreen={state.zoomControls.fullscreen}
        />
      )}

      {/* Context Menu */}
      {contextMenu.contextMenu && state.isEditMode && (
        <CanvasContextMenu
          x={contextMenu.contextMenu.x}
          y={contextMenu.contextMenu.y}
          items={contextMenu.contextMenuItems}
          onClose={() => contextMenu.setContextMenu(null)}
        />
      )}

      {/* Konva Canvas Container */}
      <div 
        ref={state.containerRef} 
        style={{ width: '100%', height: '100%' }}
      />

      {/* Text Editor and Format Toolbars Overlay */}
      <CanvasOverlays
        textEditor={state.textEditor}
        editingElement={helpers.editingElement}
        editingElementBounds={helpers.editingElementBounds}
        stageTransform={state.stageTransform}
        showFormatToolbar={showFormatToolbar}
        containerRef={state.containerRef}
        historyManagerRef={state.historyManagerRef}
      />

      {/* Connector and Shape Style Toolbars */}
      <CanvasToolbars
        isEditMode={state.isEditMode}
        selectedIds={state.selectedIds}
        elements={state.elements}
        isTransforming={state.isTransforming}
        textEditor={state.textEditor}
        getElementsScreenBounds={helpers.getElementsScreenBounds}
        containerRef={state.containerRef}
        historyManagerRef={state.historyManagerRef}
      />
    </div>
  );
};

/**
 * BoardCanvas - Production-ready canvas with direct Konva.js performance
 * 
 * Drop-in replacement for BoardCanvas with the same API but better performance.
 * Uses direct Konva.js manipulation instead of react-konva.
 */
export const BoardCanvas: React.FC<BoardCanvasInnerProps> = (props) => {
  return (
    <SelectionProvider>
      <BoardCanvasInner {...props} />
    </SelectionProvider>
  );
};

// Re-export types for convenience
export type { CanvasMode, CanvasApi } from './BoardCanvas.types';
